import { ChangeDetectorRef, Directive, Input, OnDestroy, TemplateRef, ViewContainerRef } from "@angular/core";
import { BreakpointObserver } from "@angular/cdk/layout";
import { Subscription } from "rxjs";
import { BREAKPOINT_SCREEN_LG, BREAKPOINT_SCREEN_MD, BREAKPOINT_SCREEN_SM } from "@helpers/constants";

const breakpointMapping = {
	sm: BREAKPOINT_SCREEN_SM,
	md: BREAKPOINT_SCREEN_MD,
	lg: BREAKPOINT_SCREEN_LG
};

/**
 * This directive is used to hide an element on a specific breakpoint.
 * It is used in the following way:
 * <div *hideOnBreakpoint="['sm', 'md']">This will be hidden on small and medium screens</div>
 * The directive accepts an array of strings, which are mapped to the breakpoints defined in the constants file.
 * supported breakpoints are: sm, md, lg
 */
@Directive({
	selector: "[hideOnBreakpoint]",
	standalone: false
})
export class HideOnBreakpointDirective implements OnDestroy {
	private _breakpointSubscription: Subscription = null;
	constructor(
		private _viewContainer: ViewContainerRef,
		private templateRef: TemplateRef<unknown>,
		private breakpointObserver: BreakpointObserver,
		private cdr: ChangeDetectorRef
	) {}

	private breakpoint: string[] = [BREAKPOINT_SCREEN_SM];
	private isViewCreated: boolean = false;

	@Input() set hideOnBreakpoint(breakpoints: string[]) {
		this.breakpoint = breakpoints.map((breakpoint) => breakpointMapping[breakpoint]);
		this.unsubscribe();
		if (this.breakpoint && this.breakpoint.length > 0) {
			this._updateView();
		}
	}

	private _updateView(): void {
		this._breakpointSubscription = this.breakpointObserver.observe(this.breakpoint).subscribe((result) => {
			if (result.matches && this.isViewCreated) {
				this._viewContainer.clear();
				this.cdr.markForCheck();
				this.isViewCreated = false;
			} else if (!this.isViewCreated && !result.matches) {
				this._viewContainer.createEmbeddedView(this.templateRef);
				this.cdr.markForCheck();
				this.isViewCreated = true;
			}
		});
	}

	public ngOnDestroy(): void {
		this.unsubscribe();
	}

	private unsubscribe(): void {
		if (this._breakpointSubscription) {
			this._breakpointSubscription.unsubscribe();
		}
	}
}
